import { BitmapText, Container, ContainerOptions, Graphics } from "pixi.js";
import gsap from "gsap";

export interface DigitOptions extends ContainerOptions {
  fontFamily: string;
  fontSize: number;
  spacing?: number;
  tint?: number;
  initialValue?: number;
}

export class Digit extends Container {
  private readonly digitColumn: Container;
  private readonly digitTexts: BitmapText[] = [];
  private activeTween: gsap.core.Tween | null = null;

  private readonly _fontSize: number;
  private readonly _fontFamily: string;
  private readonly _spacing: number;
  private _currentValue: number = 0;
  private _maxDigitWidth: number;
  private _maxDigitHeight: number;

  private readonly _debugGfx: Graphics | undefined;

  public _zeroVisible: boolean = true;

  constructor(options: DigitOptions) {
    super(options);

    this._fontFamily = options.fontFamily;
    this._fontSize = options.fontSize;
    this._spacing = options.spacing ?? this._fontSize;
    this._currentValue = options.initialValue ?? 0;

    // Calculate the maximum width and height of all digits
    let maxWidth = 0;
    let maxHeight = 0;
    const tempDigitTexts: BitmapText[] = [];

    for (let i = 0; i <= 9; i++) {
      const digitText = new BitmapText({
        text: i.toString(),
        style: {
          fontFamily: this._fontFamily,
          fontSize: this._fontSize,
        },
      });

      if (options.tint !== undefined) {
        digitText.tint = options.tint;
      }

      maxWidth = Math.max(maxWidth, digitText.width);
      maxHeight = Math.max(maxHeight, digitText.height);
      tempDigitTexts.push(digitText);
    }

    this._maxDigitWidth = maxWidth;
    this._maxDigitHeight = maxHeight;

    this.digitColumn = new Container();
    this.addChild(this.digitColumn);

    for (let i = 0; i <= 9; i++) {
      const digitText = tempDigitTexts[i];

      // Center the digit within the max width and height
      digitText.anchor.set(0.5, 0.5);
      digitText.x = this._maxDigitWidth / 2;
      digitText.y = i * this._spacing + this._maxDigitHeight / 2;

      this.digitTexts.push(digitText);
      this.digitColumn.addChild(digitText);
    }

    const clipMask = new Graphics();
    clipMask.rect(0, 0, this._maxDigitWidth, this._spacing).fill(0xffffff);
    this.addChild(clipMask);
    this.mask = clipMask;

    // Add debug graphics to visualize the digit boundaries
    {
      this._debugGfx = new Graphics();
      this._debugGfx
        .rect(0, 0, this._maxDigitWidth, this._spacing)
        .fill({ color: "0x00ff00cc" })
        .stroke({ width: 1, color: 0xff0000 });

      this.setPosition(this._currentValue);
    }
  }

  public setValue(value: number): void {
    this.setPosition(value);
  }

  /**
   * Set displayed digit to value
   * @param value - Valid numbers 0 - 9
   */
  public setPosition(value: number): void {
    this._currentValue = Math.max(0, Math.min(9, Math.floor(value)));

    for (let d = 0; d < 10; d++) {
      this.digitTexts[d].y = d * this._spacing + this._maxDigitHeight / 2;
    }

    this.digitColumn.y = -this._currentValue * this._spacing;
  }

  public snapTo(value: number): void {
    this.killActiveTween();
    this.setPosition(value);
  }

  public setScrollPosition(continuousValue: number): void {
    const h = this._spacing;
    const cycleH = 10 * h;
    const mod = (((continuousValue * h) % cycleH) + cycleH) % cycleH;

    this.digitColumn.y = -mod;

    for (let d = 0; d < 10; d++) {
      this.digitTexts[d].y =
        d * h < mod - h
          ? d * h + cycleH + this._maxDigitHeight / 2
          : d * h + this._maxDigitHeight / 2;
    }

    this._currentValue = Math.round(continuousValue) % 10;
  }

  public rollTo(
    targetValue: number,
    options: {
      duration: number;
      extraSpins?: number;
      easing?: gsap.EaseFunction | gsap.EaseString;
    },
  ): void {
    if (this.activeTween) {
      this.activeTween.kill();
      this.activeTween = null;
    }
    this.setPosition(this._currentValue);

    if (options.extraSpins === undefined) options.extraSpins = 0;
    if (options.easing === undefined) options.easing = "none";

    const h = this._spacing;
    const cycleHeight = 10 * h;
    const rawSteps = (targetValue - this._currentValue + 10) % 10;
    const totalSteps = rawSteps + options.extraSpins * 10;

    if (totalSteps === 0) return;

    const startDigit = this._currentValue;
    this._currentValue = targetValue;

    const proxy = { scrolled: 0 };

    this.activeTween = gsap.to(proxy, {
      scrolled: totalSteps * h,
      duration: Math.min(options.duration, 0.5 * totalSteps),
      ease: options.easing,
      onUpdate: () => {
        const virtualY = startDigit * h + proxy.scrolled;
        const mod = virtualY % cycleHeight;

        this.digitColumn.y = -mod;

        for (let d = 0; d < 10; d++) {
          this.digitTexts[d].y =
            d * h < mod - h
              ? d * h + cycleHeight + this._maxDigitHeight / 2
              : d * h + this._maxDigitHeight / 2;
        }
      },
      onComplete: () => {
        this.setPosition(targetValue);
        this.activeTween = null;
      },
    });
  }

  public killActiveTween(): void {
    if (this.activeTween) {
      this.activeTween.kill();
      this.activeTween = null;
    }
  }

  public getValue(): number {
    return this._currentValue;
  }

  public getDigitWidth(): number {
    return this._maxDigitWidth;
  }

  public getDigitHeight(): number {
    return this._maxDigitHeight;
  }

  public set zeroVisible(visible: boolean) {
    this._zeroVisible = visible;
    // Update visibility of the zero digit text
    if (this.digitTexts[0]) {
      this.digitTexts[0].visible = visible;
    }
  }

  public get zeroVisible(): boolean {
    return this._zeroVisible;
  }
}
